import { FetchContext } from 'context/FetchContext';
import { useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';

// ── Boards ──────────────────────────────────────────────────────────────────
export function useManageBoard() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();

  return useMutation(
    (data) => {
      if (data.boardId)
        return privateFetch.put(`/v1/admin/manage-podcasts/exam-info/boards/${data.boardId}`, data);
      return privateFetch.post('/v1/admin/manage-podcasts/exam-info/boards', data);
    },
    { onSettled: () => qc.invalidateQueries('exam-info-boards') }
  );
}

export function useDeleteBoard() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();
  return useMutation(
    (boardId) => privateFetch.delete(`/v1/admin/manage-podcasts/exam-info/boards/${boardId}`),
    { onSettled: () => qc.invalidateQueries('exam-info-boards') }
  );
}

// ── Exam Types ───────────────────────────────────────────────────────────────
export function useManageExamType() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();

  return useMutation(
    ({ boardId, examTypeId, notify, ...rest }) => {
      if (examTypeId)
        return privateFetch.put(`/v1/admin/manage-podcasts/exam-info/exam-types/${examTypeId}`, {
          ...rest,
          notify: !!notify,
        });
      return privateFetch.post(
        `/v1/admin/manage-podcasts/exam-info/boards/${boardId}/exam-types`,
        rest
      );
    },
    {
      onSettled: (_d, _e, vars) => {
        qc.invalidateQueries(['exam-types', vars.boardId]);
        if (vars.examTypeId) qc.invalidateQueries(['exam-type', vars.examTypeId]);
      },
    }
  );
}

// Fetches another exam type's full detail (incl. syllabus) on demand, so its syllabus
// can be loaded into the form as a starting template — no data is saved by this call.
export function useFetchExamTypeForCopy() {
  const { privateFetch } = useContext(FetchContext);
  return useMutation((examTypeId) =>
    privateFetch
      .get(`/v1/admin/manage-podcasts/exam-info/exam-types/${examTypeId}`)
      .then((r) => r.data)
  );
}

export function useReorderExamTypes() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();
  return useMutation(
    ({ boardId, rows }) =>
      privateFetch.put(
        `/v1/admin/manage-podcasts/exam-info/boards/${boardId}/exam-types/reorder`,
        rows
      ),
    { onSettled: (_d, _e, vars) => qc.invalidateQueries(['exam-types', vars.boardId]) }
  );
}

export function useDeleteExamType() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();
  return useMutation(
    ({ examTypeId, boardId }) =>
      privateFetch.delete(`/v1/admin/manage-podcasts/exam-info/exam-types/${examTypeId}`),
    { onSettled: (_d, _e, vars) => qc.invalidateQueries(['exam-types', vars.boardId]) }
  );
}

// ── Mark Distribution ────────────────────────────────────────────────────────
export function useSaveMarkDistribution() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();
  return useMutation(
    ({ examTypeId, rows }) =>
      privateFetch.post(
        `/v1/admin/manage-podcasts/exam-info/exam-types/${examTypeId}/mark-distribution`,
        { rows }
      ),
    { onSettled: (_d, _e, vars) => qc.invalidateQueries(['exam-type', vars.examTypeId]) }
  );
}

export function useDeleteMarkDistributionRow() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();
  return useMutation(
    ({ examTypeId, rowId }) =>
      privateFetch.delete(
        `/v1/admin/manage-podcasts/exam-info/exam-types/${examTypeId}/mark-distribution/${rowId}`
      ),
    { onSettled: (_d, _e, vars) => qc.invalidateQueries(['exam-type', vars.examTypeId]) }
  );
}

// ── Syllabus ─────────────────────────────────────────────────────────────────
export function useSaveSyllabus() {
  const { privateFetch } = useContext(FetchContext);
  const qc = useQueryClient();
  return useMutation(
    ({ examTypeId, rows }) =>
      privateFetch.post(`/v1/admin/manage-podcasts/exam-info/exam-types/${examTypeId}/syllabus`, {
        rows,
      }),
    { onSettled: (_d, _e, vars) => qc.invalidateQueries(['exam-type', vars.examTypeId]) }
  );
}

// ── Manual Notification ───────────────────────────────────────────────────────
export function useSendPodcastNotification() {
  const { privateFetch } = useContext(FetchContext);
  return useMutation((data) =>
    privateFetch.post('/v1/admin/manage-podcasts/exam-info/notifications/send', data)
  );
}
